import type { Domino, GameState } from '../types';
import type { GameRules } from '../layers/types';
import type { HandConstraints } from './constraint-tracker';
import {
  getCandidateDominoes,
  getDistributionPool,
  getExpectedHandSizes,
} from './constraint-tracker';
import type { SampledHands } from './hand-sampler';

const MAX_ENUMERATED_WORLDS = 48;

export interface ExactEndgameWorldSet {
  worlds: SampledHands[];
  /** True only when the valid hidden-world set contains at most 48 worlds. */
  complete: boolean;
}

export function countRemainingTiles(state: GameState): number {
  const expectedHandSizes = getExpectedHandSizes(state);
  return state.currentTrick.length + expectedHandSizes.reduce((total, size) => total + size, 0);
}

export function shouldUseExactEndgame(
  state: GameState,
  threshold: number | null,
): boolean {
  if (threshold === null) return false;
  return countRemainingTiles(state) <= threshold;
}

function enumerateAssignments(
  opponents: number[],
  remaining: Map<number, number>,
  available: Set<string>,
  candidatesPerOpponent: Map<number, Set<string>>,
  handIds: Map<number, string[]>,
  results: SampledHands[],
  resultKeys: Set<string>,
  pool: Domino[],
  maxWorlds: number,
): void {
  if (results.length >= maxWorlds) return;

  let mostConstrained: number | null = null;
  let minSlack = Infinity;

  for (const opp of opponents) {
    const need = remaining.get(opp) ?? 0;
    if (need === 0) continue;

    const candidates = candidatesPerOpponent.get(opp)!;
    let availableCount = 0;
    for (const id of candidates) {
      if (available.has(id)) availableCount += 1;
    }
    const slack = availableCount - need;
    if (slack < 0) return;
    if (slack < minSlack) {
      minSlack = slack;
      mostConstrained = opp;
    }
  }

  if (mostConstrained === null) {
    const hands = new Map<number, Domino[]>();
    for (const opp of opponents) {
      const ids = [...(handIds.get(opp) ?? [])].sort();
      hands.set(
        opp,
        ids.map((id) => pool.find((domino) => String(domino.id) === id)!),
      );
    }
    const key = worldOrderKey(hands);
    if (!resultKeys.has(key)) {
      resultKeys.add(key);
      results.push(hands);
    }
    return;
  }

  const candidates = [...candidatesPerOpponent.get(mostConstrained)!]
    .filter((id) => available.has(id))
    .sort();

  for (const candidateId of candidates) {
    available.delete(candidateId);
    handIds.get(mostConstrained)!.push(candidateId);
    remaining.set(mostConstrained, (remaining.get(mostConstrained) ?? 1) - 1);

    enumerateAssignments(
      opponents,
      remaining,
      available,
      candidatesPerOpponent,
      handIds,
      results,
      resultKeys,
      pool,
      maxWorlds,
    );

    available.add(candidateId);
    handIds.get(mostConstrained)!.pop();
    remaining.set(mostConstrained, (remaining.get(mostConstrained) ?? 0) + 1);

    if (results.length >= maxWorlds) return;
  }
}

/**
 * Enumerate valid hidden-world assignments when the remaining tile count is small.
 * Returns completeness separately because the evaluator keeps the same bounded
 * prefix for current play behavior even when more valid worlds exist.
 */
export function enumerateExactEndgameWorlds(
  state: GameState,
  constraints: HandConstraints,
  rules: GameRules,
  threshold: number | null,
): ExactEndgameWorldSet | null {
  if (!shouldUseExactEndgame(state, threshold)) {
    return null;
  }

  const pool = getDistributionPool(constraints);
  if (threshold === null || pool.length > threshold) {
    return null;
  }

  const myIndex = constraints.myPlayerIndex;
  const opponents = [0, 1, 2, 3].filter((index) => index !== myIndex);
  const expectedSizes = getExpectedHandSizes(state);
  const available = new Set(pool.map((domino) => String(domino.id)));
  const candidatesPerOpponent = new Map<number, Set<string>>();

  for (const opp of opponents) {
    const candidates = getCandidateDominoes(constraints, opp, state, rules);
    candidatesPerOpponent.set(opp, new Set(candidates.map((domino) => String(domino.id))));
  }

  const remaining = new Map<number, number>();
  const handIds = new Map<number, string[]>();
  for (const opp of opponents) {
    remaining.set(opp, expectedSizes[opp] ?? 0);
    handIds.set(opp, []);
  }

  const results: SampledHands[] = [];
  const resultKeys = new Set<string>();
  enumerateAssignments(
    opponents,
    remaining,
    available,
    candidatesPerOpponent,
    handIds,
    results,
    resultKeys,
    pool,
    MAX_ENUMERATED_WORLDS + 1,
  );

  const complete = results.length <= MAX_ENUMERATED_WORLDS;
  if (!complete) results.pop();

  // Enumeration order is part of the decision input. Keep the bounded exact
  // set's order explicit for deterministic replay.
  results.sort(compareWorldOrder);
  return { worlds: results, complete };
}

function compareWorldOrder(left: SampledHands, right: SampledHands): number {
  const leftKey = worldOrderKey(left);
  const rightKey = worldOrderKey(right);
  if (leftKey < rightKey) return -1;
  if (leftKey > rightKey) return 1;
  return 0;
}

function worldOrderKey(world: SampledHands): string {
  return [...world.entries()]
    .sort(([left], [right]) => left - right)
    .map(([player, hand]) => {
      const dominoIds = hand.map((domino) => String(domino.id)).sort();
      return `${player}:${dominoIds.join(',')}`;
    })
    .join('|');
}
